import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'

// Third party libraries
import { produce } from 'immer'

// ** Type Imports
import {
  ActivePeriodLengthType,
  ApplicationStageType,
  CheckboxUIConfigType,
  DiscountEditorDataType,
  DiscountFormErrorState,
  DiscountFormStateType,
  DiscountPreviewConfigType,
  DiscountPreviewUIConfigType,
  DiscountUsageLimitsConfigType,
  DiscountUsageLimitsUIConfigType,
  GenerationType,
  InputUIConfigType
} from 'src/lib/types/discounts'

// ** Util Imports
import InputTextField from 'src/components/common/form-elements/InputTextField'
import { createDiscountPayload, getDefaultHeaderForPaymentOffer, getUIConfig } from 'src/lib/utils/discountHelpers'

// ** Child Imports
import FormTile from 'src/components/common/containers/FormTile'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'
import { Typography } from '@mui/material'
import AdvanceDateTimeInput from 'src/components/common/form-elements/AdvanceDateTimeInput'
import DiscountDescriptionEditor from '../../discount-description-editor/DiscountDescriptionEditor'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { alertSeverites } from 'src/lib/utils/constants'
import { fetchCouponDescription } from 'src/lib/core/api-methods'
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import { useLocale } from 'src/lib/hooks/useLocal'
import Permission from 'src/services/PermissionService'
import { permissionType } from 'src/lib/utils/constants'
import FloRadioGroup from 'src/components/common/form-elements/FloRadioGroup'
import { Info } from 'react-feather'
import { errorMappings } from 'src/lib/utils/validations/discountsValidations'
import { defaultErrorState } from 'src/assets/DiscountsConfig'
import { EntityName } from 'src/lib/hooks/useActivityLogs'
import { RadioItemConfigType } from 'src/lib/types/random'

const initialEditorState: DiscountEditorDataType = {
  couponId: '',
  identifier: '',
  descriptionTitle: '',
  description: '',
  longDescription: []
}

interface DiscountPreviewFormProps {
  uiConfig: DiscountPreviewUIConfigType
  setFormState: Dispatch<SetStateAction<DiscountFormStateType | undefined>>
  formState: DiscountFormStateType | undefined
  isEditMode: boolean
  generationType: GenerationType | undefined
  saveTriggered?: boolean
  formErrors: DiscountFormErrorState
  setFormErrors: Dispatch<SetStateAction<DiscountFormErrorState>>
  applicationStage?: ApplicationStageType
}

const DiscountPreviewForm: React.FC<DiscountPreviewFormProps> = ({
  uiConfig,
  formState,
  isEditMode,
  setFormState,
  generationType,
  saveTriggered,
  formErrors,
  setFormErrors,
  applicationStage
}) => {
  const { isDiscoverable, descriptionConfig } = formState?.preview || {}

  const { visibilityUIConfig, previewUIConfig } = uiConfig || {}

  const { t } = useLocale()
  const {
    getters: { merchant }
  } = useMerchant()
  const issuerId = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [editorData, setEditorData] = useState<DiscountEditorDataType>(initialEditorState)

  const getCouponDescription = async () => {
    if (!merchant) return
    const payload = createDiscountPayload(formState, merchant, isEditMode)
    try {
      const discountResponse: any = await fetchCouponDescription(issuerId, merchant?.accountId, payload)
      //
      if (discountResponse?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, discountResponse.error)
        console.error('x-trace-id:', discountResponse?.headers?.['x-trace-id'])
        return
      }
      setFormState(
        produce((draft: any) => {
          draft.preview.descriptionConfig.descriptionTitle = discountResponse?.data?.data?.header
          draft.preview.descriptionConfig.description = discountResponse?.data?.data?.description
          draft.preview.descriptionConfig.longDescription = discountResponse?.data?.data?.long_description
        })
      )
    } catch (err) {
      console.error(err)
      handleAlerts(alertSeverites.SEVERITY_ERROR, t('something_went_wrong'))
    }
  }

  useEffect(() => {
    if (typeof merchant === 'undefined' || isEditMode || applicationStage === 'PRE_PAYMENT_VALIDATION') return
    getCouponDescription()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant])

  useEffect(() => {
    if (typeof formState?.setup === 'undefined' || typeof descriptionConfig === 'undefined') return

    let modifiedLongDesc
    if (typeof descriptionConfig?.longDescription === 'string') {
      modifiedLongDesc = descriptionConfig?.longDescription?.split('\n')
    } else {
      modifiedLongDesc = descriptionConfig?.longDescription
    }
    const parsedEditorData: DiscountEditorDataType = {
      couponId: formState?.discountId,
      identifier: formState?.setup?.applicationMethodConfig?.value,
      descriptionTitle: descriptionConfig?.descriptionTitle,
      description: descriptionConfig?.description,
      longDescription: modifiedLongDesc
    }
    setEditorData(parsedEditorData)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState])

  const handleCustomizationSave = (descTitle: string, desc: string, longDesc: string) => {
    const customizedDescriptions = {
      descriptionTitle: descTitle,
      description: desc,
      longDescription: longDesc
    }
    setFormState(
      produce((draft: any) => {
        draft.preview.descriptionConfig = customizedDescriptions
      })
    )
  }

  const renderDiscoverabilitySection = () => {
    return (
      <FormTile title={visibilityUIConfig?.title}>
        <div className='flex flex-col w-full'>
          <FloCheckbox
            value={Boolean(isDiscoverable)}
            label={visibilityUIConfig?.visibilityCheckbox?.label}
            setValue={(checked: boolean) => {
              setFormState(
                produce((draft: any) => {
                  draft.preview.isDiscoverable = checked
                })
              )
            }}
          />
        </div>
      </FormTile>
    )
  }

  // ** Alert Handlers ** //
  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (_event: any, reason: string) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }

  const paymentOfferTitleTypeOptions: RadioItemConfigType[] = [
    {
      label: (
        <div className='flex flex-col w-[20rem] space-y-2 mt-1'>
          <span className='text-sm font-medium'>Default</span>
          {formState?.setup?.applicationConfig?.offerValueConfig?.isCustomHeader === 'DEFAULT' && (
            <InputTextField
              textFieldId='text'
              inputValue={getDefaultHeaderForPaymentOffer(
                formState?.setup?.applicationConfig?.offerValueConfig?.valueType
              )}
              inputPlaceholder={t('earn_message_line_1')}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {}}
              delimiters={[['<', '>']]}
              inputType='text'
              isDisabled={true}
              customClass='w-full'
              label='Title'
              inputClass='bg-white'
            />
          )}
        </div>
      ),
      valueType: 'DEFAULT'
    },
    {
      label: (
        <div className='flex flex-col w-[20rem] space-y-2 mt-1'>
          <span className='text-sm font-medium'>Customize payment offer title</span>
          {formState?.setup?.applicationConfig?.offerValueConfig?.isCustomHeader === 'CUSTOM' && (
            <InputTextField
              textFieldId='text'
              inputValue={formState?.setup?.applicationConfig?.discountDescription?.discountHeader}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const enteredValue = event?.target?.value
                if (enteredValue.length > 36) return
                setFormState(
                  produce((draft: any) => {
                    draft.setup.applicationConfig.discountDescription.discountHeader = enteredValue
                  })
                )
              }}
              showCharacterCountConfig={{
                maxLength: 36,
                showOnlyOnFocus: false,
                customClass: 'text-xs font-normal text-gray-500 leading-tight'
              }}
              delimiters={[['<', '>']]}
              inputType='text'
              customClass='w-full'
              label='Title'
              isFullWidth={true}
              inputClass='bg-white'
              errorStatus={formErrors?.discountHeader?.status}
              errorMessage={errorMappings?.[formErrors?.discountHeader?.errorCode as keyof typeof errorMappings]}
              handleFocus={() => setFormErrors({ ...formErrors, discountHeader: defaultErrorState })}
            />
          )}
        </div>
      ),
      valueType: 'CUSTOM'
    }
  ]

  return (
    <>
      {applicationStage === 'PRE_PAYMENT_VALIDATION' && (
        <FormTile
          title={'Payment offer title on checkout'}
          subtitle='Title will be shown on all eligible payment methods'
        >
          <>
            <FloRadioGroup
              selectedOption={formState?.setup?.applicationConfig?.offerValueConfig?.isCustomHeader}
              options={paymentOfferTitleTypeOptions}
              groupName={'method-of-application'}
              customClassName='flex flex-col space-y-4'
              setSelectedOption={(selectedOption: any) => {
                setFormState(
                  produce((draft: any) => {
                    draft.setup.applicationConfig.offerValueConfig.isCustomHeader = selectedOption
                    if (selectedOption === 'CUSTOM') {
                      draft.setup.applicationConfig.discountDescription.discountHeader = ''
                    }
                  })
                )
              }}
            />
            {formState?.setup?.applicationConfig?.offerValueConfig?.isCustomHeader === 'CUSTOM' && (
              <div className='inline-flex gap-2 text-sm border rounded border-[#C7981F66] bg-[#C7981F1A] w-full p-3 items-center'>
                <div className='w-4'>
                  <Info className='text-[#C7981F] w-4 h-4' />
                </div>
                <p className='text-carbon inline-block justify-center items-center'>
                  {'Copy and paste'}

                  <span className='font-medium p-1 rounded-xl mx-2 border-1 border-[#C7981F66] bg-white'>
                    {'<value>'}
                  </span>
                  {'to add the percentage of this offer in this title'}
                </p>
              </div>
            )}
          </>
        </FormTile>
      )}
      {applicationStage !== 'PRE_PAYMENT_VALIDATION' && (
        <>
          <div id='discoverability' className={`${visibilityUIConfig?.isHidden ? 'hidden' : ''}`}>
            {renderDiscoverabilitySection()}
          </div>

          <div id='preview'>
            <FormTile title={previewUIConfig?.title}>
              <Permission permissionId={permissionType.CHANGE_COUPON_DESCRIPTION}>
                <DiscountDescriptionEditor
                  isDialog={false}
                  handleSave={handleCustomizationSave}
                  saveTriggered={saveTriggered}
                  editorData={editorData}
                />
              </Permission>
            </FormTile>
          </div>
        </>
      )}
      {/* Alerts */}
      {openAlert.length ? (
        <Alerts
          open={Boolean(openAlert.length)}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : (
        <></>
      )}
    </>
  )
}

export default DiscountPreviewForm
